import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Input } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { Rnd } from "react-rnd";
import Button from "@lhd/components/Button";
import { Highlight, Column, Row, ReactGridProps } from "@silevis/reactgrid";

type PanelState = {
  x: number;
  y: number;
  width: number;
  height: number;
  isOpen: boolean;
};

type PersistedState = Omit<PanelState, "isOpen">;
const STORAGE_KEY = "panelCtrlH_state";

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const clampStateToViewport = (state: PanelState): PanelState => {
  if (typeof window === "undefined") return state;
  const maxX = Math.max(0, window.innerWidth - state.width);
  const maxY = Math.max(0, window.innerHeight - state.height);
  return {
    ...state,
    x: clamp(state.x, 0, maxX),
    y: clamp(state.y, 0, maxY),
  };
};

const getDefaultState = (): PanelState => {
  if (typeof window === "undefined") {
    return { x: 0, y: 100, width: 360, height: 240, isOpen: false };
  }

  return {
    x: 0,
    y: 100,
    width: Math.min(window.innerWidth, 400),
    height: Math.min(window.innerHeight, 220),
    isOpen: false,
  };
};

const loadState = (): PanelState => {
  if (typeof window === "undefined") {
    return getDefaultState();
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return getDefaultState();
    const parsed = JSON.parse(raw) as Partial<PersistedState>;
    const merged = {
      ...getDefaultState(),
      ...parsed,
      isOpen: false,
    };
    return clampStateToViewport(merged);
  } catch {
    return getDefaultState();
  }
};

interface IPanelCtrlHProps {
  rows: Row<any>[];
  columns: Column[];
  handleChangeHighlight: (rows: any[]) => void;
  onCellsChanged?: ReactGridProps["onCellsChanged"];
  scrollToCell: (cell: { rowId: string; columnId: string }) => void;
  nameTable?: string;
}

const PanelCtrlH = ({
  rows,
  columns,
  handleChangeHighlight,
  onCellsChanged,
  scrollToCell,
  nameTable,
}: IPanelCtrlHProps) => {
  const [panelState, setPanelState] = useState<PanelState>(() => loadState());
  const { x, y, width, height, isOpen } = panelState;

  const updatePanelState = (
    next: Partial<PanelState> | ((prev: PanelState) => PanelState),
  ) => {
    setPanelState((prev) =>
      typeof next === "function" ? next(prev) : { ...prev, ...next },
    );
  };
  const [search, setSearch] = useState("");
  const [replace, setReplace] = useState("");
  const [matches, setMatches] = useState<
    Array<{ rowId: Row<any>["rowId"]; columnId: Column["columnId"] }>
  >([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
  const [lastSearchTerm, setLastSearchTerm] = useState("");

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "h") {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        updatePanelState((prev) =>
          clampStateToViewport({ ...prev, isOpen: true }),
        );
      }
    };

    window.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => {
      window.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleResize = () => {
      updatePanelState((prev) => clampStateToViewport(prev));
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const persisted: PersistedState = {
      x: panelState.x,
      y: panelState.y,
      width: panelState.width,
      height: panelState.height,
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
  }, [panelState]);

  const buildMatches = (term: string) => {
    const matchedCells: Array<{
      rowId: Row<any>["rowId"];
      columnId: Column["columnId"];
    }> = [];
    rows.forEach((row) => {
      if (row.rowId !== "header") {
        row.cells.forEach((cell, index) => {
          const columnId = columns[index]?.columnId;
          if (!columnId || columnId === "index") return;
          if (cell.text?.includes(term)) {
            matchedCells.push({ rowId: row.rowId, columnId });
          }
        });
      }
    });
    return matchedCells;
  };

  const handleSearchAll = () => {
    const term = search;
    if (!term) {
      handleChangeHighlight([]);
      setMatches([]);
      setCurrentMatchIndex(-1);
      setLastSearchTerm("");
      return;
    }
    const matchedCells = buildMatches(term);
    const matched: Array<Highlight> = matchedCells.map((item) => ({
      rowId: item.rowId,
      columnId: item.columnId,
      borderColor: "#f90669",
      className: "matched",
    }));
    setMatches(matchedCells);
    setCurrentMatchIndex(-1);
    setLastSearchTerm(term);
    handleChangeHighlight(matched);
  };

  const handleSearchOneByOne = () => {
    const term = search.trim();
    if (!term) {
      handleChangeHighlight([]);
      setMatches([]);
      setCurrentMatchIndex(-1);
      setLastSearchTerm("");
      return;
    }

    const shouldRebuild =
      term !== lastSearchTerm || (term === lastSearchTerm && !matches.length);
    const nextMatches = shouldRebuild ? buildMatches(term) : matches;

    if (!nextMatches.length) {
      handleChangeHighlight([]);
      setMatches([]);
      setCurrentMatchIndex(-1);
      setLastSearchTerm(term);
      return;
    }

    const nextIndex =
      currentMatchIndex < 0 ? 0 : (currentMatchIndex + 1) % nextMatches.length;
    const target = nextMatches[nextIndex];

    handleChangeHighlight([
      {
        rowId: target.rowId,
        columnId: target.columnId,
        borderColor: "#f90669",
        className: "matched",
      },
    ]);

    // scrollToCell({
    //   rowId: target.rowId.toString(),
    //   columnId: target.columnId.toString(),
    // });

    setMatches(nextMatches);
    setCurrentMatchIndex(nextIndex);
    setLastSearchTerm(term);
  };

  const applyReplace = (replaceAll: boolean) => {
    const term = search;
    if (!term || !onCellsChanged) return;

    const changes: any[] = [];
    if (!replaceAll) {
      if (currentMatchIndex < 0 || !matches.length) return;
      const target = matches[currentMatchIndex];
      const rowIndex = rows.findIndex((row) => row.rowId === target.rowId);
      const columnIndex = columns.findIndex(
        (column) => column.columnId === target.columnId,
      );
      if (rowIndex < 0 || columnIndex < 0) return;
      const row = rows[rowIndex];
      const cell = row.cells[columnIndex];
      if (!cell || cell.nonEditable) return;
      const text = typeof cell.text === "string" ? cell.text : "";
      if (!text.includes(term)) return;

      const newText = text.replace(term, replace);
      if (newText === text) return;

      changes.push({
        rowId: row.rowId,
        columnId: target.columnId,
        type: cell.type,
        previousCell: cell as any,
        newCell: { ...(cell as any), text: newText },
      });
    } else {
      for (const row of rows) {
        if (row.rowId === "header") continue;
        for (let index = 0; index < row.cells.length; index += 1) {
          const cell = row.cells[index];
          if (cell.nonEditable) continue;
          const text = typeof cell.text === "string" ? cell.text : "";
          if (!text.includes(term)) continue;
          const columnId = columns[index]?.columnId;
          if (columnId === undefined || columnId === null) continue;

          const newText = text.split(term).join(replace);
          if (newText === text) continue;

          changes.push({
            rowId: row.rowId,
            columnId,
            type: cell.type,
            previousCell: cell as any,
            newCell: { ...(cell as any), text: newText },
          });
        }
      }
    }

    if (!changes.length) return;
    const replacedHighlights: Highlight[] = changes.map((change) => ({
      rowId: change.rowId,
      columnId: change.columnId,
      borderColor: "#3579f8",
      className: "matched",
    }));
    // if (replacedHighlights.length === 1) {
    //   scrollToCell({
    //     rowId: replacedHighlights[0].rowId.toString(),
    //     columnId: replacedHighlights[0].columnId.toString(),
    //   });
    // }
    onCellsChanged(changes as any);
    handleChangeHighlight(replacedHighlights);
  };
  useEffect(() => {
    if (!isOpen) {
      handleChangeHighlight([]);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <Rnd
      bounds="window"
      size={{ width, height }}
      position={{ x, y }}
      onDragStop={(_, data) => {
        updatePanelState({ x: data.x, y: data.y });
      }}
      onResizeStop={(_, __, ref, ___, position) => {
        updatePanelState({
          width: ref.offsetWidth,
          height: ref.offsetHeight,
          x: position.x,
          y: position.y,
        });
      }}
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        border: "1px solid #ccc",
        borderRadius: 8,
        background: "#fff",
        boxShadow: "0 12px 24px rgba(0, 0, 0, 0.12)",
        padding: "10px",
        zIndex: 1000,
        overflow: "hidden",
      }}
    >
      <div style={{ position: "relative", height: "100%" }}>
        <div
          onClick={() => updatePanelState({ isOpen: false })}
          style={{ position: "absolute", top: 0, right: 0, cursor: "pointer" }}
        >
          <CloseOutlined />
        </div>
        <h3>検索と置換 {nameTable ? `(${nameTable})` : ""}</h3>
        <div onMouseDown={(e) => e.stopPropagation()}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <label style={{ width: 35 }}>検索</label>
              <Input
                placeholder="検索"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={false}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <label style={{ width: 35 }}>置換</label>
              <Input
                placeholder="置換"
                value={replace}
                onChange={(e) => setReplace(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div style={{ fontSize: 18 }}>
          <i>
            検索結果は
            <span style={{ fontWeight: "bold" }}>{matches.length}</span>
            件です
          </i>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 10,
            marginTop: 10,
          }}
        >
          {/* <Button
            type="primary"
            label="find one"
            disabled={!search}
            onClick={handleSearchOneByOne}
          />
          <Button
            label="置換"
            disabled={!search || !replace || currentMatchIndex < 0}
            onClick={() => applyReplace(false)}
          >
            置換
          </Button> */}
          <Button
            type="primary"
            label="検索"
            disabled={!search}
            onClick={handleSearchAll}
          >
            検索
          </Button>
          <Button
            type="primary"
            label="すべて置換"
            disabled={!search || !replace}
            onClick={() => applyReplace(true)}
          >
            すべて置換
          </Button>
          <Button
            type="primary"
            label="閉じる"
            onClick={() => updatePanelState({ isOpen: false })}
          >
            閉じる
          </Button>
        </div>
      </div>
    </Rnd>,
    document.body,
  );
};

export default PanelCtrlH;
